import { useEffect, useRef, useState } from "react";
import { getCSRFToken } from "../components/utils/various";
import { ProvisionalContentMedium } from "../types";

type Callbacks = {
  // Job accepté par le backend : on peut fermer la modale et afficher un placeholder.
  onStarted: (jobId: string) => void;
  // Progression simulée tant que le job tourne (on n'a pas de vraie progression).
  onProgress: (jobId: string, progress: number) => void;
  // Génération terminée : cm = ContentMedium sérialisé (serialized_for_publisher).
  onComplete: (jobId: string, cm: ProvisionalContentMedium) => void;
  onError: (jobId: string, message: string) => void;
};

const POLL_INTERVAL = 2500;
const PROGRESS_CAP = 90;
const PROGRESS_STEP = 12;

/**
 * Lance une génération d'image par IA côté Rails (Sidekiq) puis poll le statut
 * en arrière-plan. Plusieurs générations peuvent tourner en parallèle (un timer
 * par job). `loading`/`error` ne couvrent que la requête de lancement (modale).
 */
export default function useAiImageGeneration(cb: Callbacks) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const timersRef = useRef<Map<string, number>>(new Map());

  const stopJob = (jobId: string) => {
    const timer = timersRef.current.get(jobId);
    if (timer !== undefined) {
      clearInterval(timer);
      timersRef.current.delete(jobId);
    }
  };

  useEffect(
    () => () => {
      timersRef.current.forEach((t) => clearInterval(t));
      timersRef.current.clear();
    },
    [],
  );

  const poll = (jobId: string) => {
    let progress = 0;
    const timer = window.setInterval(async () => {
      progress = Math.min(progress + PROGRESS_STEP, PROGRESS_CAP);
      cbRef.current.onProgress(jobId, progress);
      try {
        const res = await fetch(`/ai_image_generations/${jobId}`, {
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!res.ok) return; // 404 tant que le worker n'a rien écrit / TTL expiré
        const data = await res.json();
        if (data.status === "done") {
          stopJob(jobId);
          cbRef.current.onComplete(jobId, data.content_medium);
        } else if (data.status === "failed") {
          stopJob(jobId);
          const message = data.error || "La génération de l'image a échoué";
          setError(message);
          cbRef.current.onError(jobId, message);
        }
      } catch {
        // erreur réseau ponctuelle : on laisse le polling continuer
      }
    }, POLL_INTERVAL);
    timersRef.current.set(jobId, timer);
  };

  const generate = async (prompt: string) => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/ai_image_generations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({ prompt }),
      });
      if (res.status === 402) {
        setError("Crédits insuffisants pour générer une image");
        return;
      }
      if (!res.ok) throw new Error("Erreur lors du lancement de la génération");
      const { job_id } = await res.json();
      cbRef.current.onStarted(job_id);
      poll(job_id);
    } catch (e: any) {
      setError(e.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return { generate, loading, error };
}
